import React, { useEffect, useState } from 'react'
import { useLocation, useHistory } from "react-router-dom";
import { isEmpty, pathOr, compose, head, isNil } from 'ramda'
import { renderData } from '../../helpers'
import { useFetch } from '../../hooks'
import Container from '../../components/Container'
import HeadBar from '../../modules/HeadBar'
import { StyledReports, StyledReport } from './styled'
import Chart from './components/Chart'
import ReportsTable from './components/ReportsTable';
import PageLoader from '../../components/PageLoader';
import ButtonDouble from '../../components/inputs/ButtonDouble';
import Icon from '../../components/Icon';
import { Store } from '../../context/store';
import { propEq, findIndex, inc, length } from 'ramda';
import FooterBar from '../../modules/FooterBar';
import useBuffer from '../../hooks/useBuffer';
import Modal from '../../components/Modal';
import Button from '../../components/inputs/Button';
import FormGenerateReport from '../../modules/forms/FormGenerateReport';

const Reports = () => {
    const history = useHistory();
    const { state: { cowsList, groupsList, zonesList } } = Store();
    const { state: { type, dataKey, icon, data, endpointExecute, refetch, report: reportFromLocation } } = useLocation();
    const [report, setReport] = useState({ data: [] });

    const getListBuffer = typeReport => {
        switch (typeReport) {
            case "cow":
                return cowsList;
            case "grp":
                return groupsList;
            case "zone":
                return zonesList;
            default:
                return [];
        }
    }

    const { error, fetching: fetchingReports, doFetch: doFetchReports } = useFetch({
        url: `/rpc/reportresult`,
        method: 'POST'
    })

    const { fetching: fetchingReportExecute, doFetch: doFetchReportExecute } = useFetch({
        method: 'POST'
    })

    const doFetch = ({ item, loader }) => new Promise(async (resolve, reject) => {
        const resExecute = await doFetchReportExecute({
            url: `/rpc/${endpointExecute}`,
            data: item !== undefined ?
                {
                    ...data,
                    [`${type}id`]: item[dataKey]
                }
                :
                {
                    ...data
                },
            loader
        })
        if (resExecute) {
            const resReports = await doFetchReports({
                data: {
                    resultkey: resExecute
                },
                loader
            })
            resolve(resReports)
        }
    })


    const { index, setIndex, refetchBuffer, onClickPrevItem, onClickNextItem } = useBuffer({
        list: getListBuffer(type),
        doFetch: doFetch,
        setItem: item => setReport(item),
        id: dataKey
    })

    useEffect(() => {
        refetch && refetchBuffer();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refetch])

    useEffect(() => {
        if (index === null) {
            const currIndex = findIndex(propEq(dataKey, data[`${type}id`]), getListBuffer(type));
            setIndex(currIndex);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [index])

    useEffect(() => {
        if (!isEmpty(report.data)) {
            history.replace({
                pathname: `/reports/${type}`,
                state: {
                    type,
                    dataKey,
                    icon,
                    data: {
                        ...data,
                        [`${type}id`]: report[dataKey]
                    },
                    endpointExecute,
                    refetch: false,
                    report: reportFromLocation
                }
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [report])

    const getInitialDataForm = () => {
        const dates = data && data.dates.replace(/{|}/g, "").split(",");
        const reports = data && data.reports.replace(/{|}/g, "").split(",");
        const last = data && data.lastweek ? "lastweek" : data.lastmonth ? "lastmonth" : null;
        return {
            startDate: dates && dates[0],
            endDate: dates && dates[1],
            reports,
            last,
        }
    }

    const getTitle = compose(
        pathOr("", ['title']),
        head,
        pathOr([], ['data'])
    );

    const renderDataState = renderData({ data: report.data, error, fetching: fetchingReports || fetchingReportExecute });

    return (
        <StyledReports>
            <HeadBar
                title={getTitle(report)}
            >
                <Modal
                    autoClose={refetch}
                    renderOpenButton={
                        ({ handleToggleIsOpenModal }) => (
                            <Button transparent noMargin onClick={handleToggleIsOpenModal}>
                                <Icon name='expand' />
                            </Button>
                        )
                    }
                >
                    {
                        ({ handleToggleIsOpenModal }) => (
                            <FormGenerateReport
                                type={type}
                                dataKey={dataKey}
                                icon={icon}
                                id={data[`${type}id`]}
                                report={reportFromLocation}
                                endpointExecute={endpointExecute}
                                isHistoryReplace={true}
                                initialData={getInitialDataForm()}
                                onClickCancel={handleToggleIsOpenModal}
                            />
                        )
                    }
                </Modal>
            </HeadBar>
            <Container>
                {
                    renderDataState({
                        fetching: () => (<PageLoader />),
                        error: error => <div>{error.toString()}</div>,
                        data: data => (
                            !isNil(data) && !isEmpty(data) &&
                            data.map((item, itemIndex) => (
                                <StyledReport
                                    key={itemIndex}
                                >
                                    {
                                        item.chart &&
                                        <Chart
                                            config={item.chart}
                                        />
                                    }
                                    {
                                        item.table &&
                                        <ReportsTable
                                            table={item.table}
                                        />
                                    }
                                </StyledReport>
                            ))
                        )
                    })
                }
            </Container>
            <FooterBar noDivider>
                {
                    length(getListBuffer(type)) !== 0 &&
                    <ButtonDouble style={{ marginLeft: 'auto' }} onClickLeft={onClickPrevItem} onClickRight={onClickNextItem} disabled={length(getListBuffer(type)) === 1}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <Icon width="24" height="24" name={icon} />
                            <div style={{ fontSize: '12px' }}>{`${index !== null && index > -1 ? inc(index) : '-'}/${length(getListBuffer(type))}`}</div>
                        </div>
                    </ButtonDouble>
                }
            </FooterBar>
        </StyledReports>
    )
}

export default Reports